import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Users, 
  Upload, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Download,
  Loader2
} from "lucide-react";

const AdminBulkEnroll = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [emailsText, setEmailsText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, {
        withCredentials: true
      });
      setCourses(response.data);
    } catch (error) {
      toast.error("Failed to load courses");
    }
  };

  const handleBulkEnrollText = async () => {
    if (!selectedCourse) {
      toast.error("Please select a course");
      return;
    }

    const emails = emailsText
      .split(/[\n,;]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));

    if (emails.length === 0) {
      toast.error("No valid emails found");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/bulk-enroll`, {
        course_id: selectedCourse,
        user_emails: emails
      }, { withCredentials: true });

      setResults(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk enrollment failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEnrollFile = async () => {
    if (!selectedCourse) {
      toast.error("Please select a course");
      return;
    }

    if (!file) {
      toast.error("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append('course_id', selectedCourse);
    formData.append('file', file);

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/bulk-enroll-file`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResults(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk enrollment failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = "email\nstudent1@example.com\nstudent2@example.com\n";
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_enrollment_template.csv';
    a.click();
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="bulk-enroll-page">
        <div>
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Bulk Enrollment
          </h1>
          <p className="text-stone-600 mt-2">Enroll multiple students in a course at once</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Enroll Students</CardTitle>
              <CardDescription>Select a course and add student emails</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Course Selection */}
              <div className="space-y-2">
                <Label>Select Course</Label>
                <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                  <SelectTrigger data-testid="course-select">
                    <SelectValue placeholder="Choose a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.course_id} value={course.course_id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tabs for different input methods */}
              <Tabs defaultValue="text">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="text">
                    <FileText className="h-4 w-4 mr-2" />
                    Enter Emails
                  </TabsTrigger>
                  <TabsTrigger value="file">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload File
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="text" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Addresses</Label>
                    <Textarea
                      placeholder="Enter email addresses (one per line, or separated by commas)"
                      value={emailsText}
                      onChange={(e) => setEmailsText(e.target.value)}
                      className="h-48"
                      data-testid="emails-textarea"
                    />
                    <p className="text-sm text-stone-500">
                      Separate emails by new lines, commas, or semicolons
                    </p>
                  </div>
                  <Button 
                    onClick={handleBulkEnrollText}
                    disabled={loading || !selectedCourse}
                    className="bg-violet-700 hover:bg-violet-800"
                    data-testid="enroll-text-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Users className="h-4 w-4 mr-2" />
                    )}
                    Enroll Students
                  </Button>
                </TabsContent>

                <TabsContent value="file" className="space-y-4">
                  <div className="space-y-2">
                    <Label>Upload CSV or TXT File</Label>
                    <div className="border-2 border-dashed border-stone-200 rounded-lg p-6 text-center">
                      <Input
                        type="file"
                        accept=".csv,.txt"
                        onChange={(e) => setFile(e.target.files[0])}
                        className="hidden"
                        id="file-upload"
                      />
                      <label 
                        htmlFor="file-upload" 
                        className="cursor-pointer"
                      >
                        <Upload className="h-10 w-10 text-stone-400 mx-auto mb-3" />
                        <p className="text-stone-600 mb-1">
                          {file ? file.name : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-sm text-stone-500">CSV or TXT file with emails</p>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleBulkEnrollFile}
                      disabled={loading || !selectedCourse || !file}
                      className="bg-violet-700 hover:bg-violet-800"
                      data-testid="enroll-file-btn"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Users className="h-4 w-4 mr-2" />
                      )}
                      Enroll from File
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={downloadTemplate}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Results Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Results</CardTitle>
              <CardDescription>Enrollment status</CardDescription>
            </CardHeader>
            <CardContent>
              {results ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {results.summary.enrolled}
                      </p>
                      <p className="text-sm text-green-600">Enrolled</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-700">
                        {results.summary.already_enrolled}
                      </p>
                      <p className="text-sm text-orange-600">Already Enrolled</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-700">
                        {results.summary.user_not_found}
                      </p>
                      <p className="text-sm text-red-600">Not Found</p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-stone-700">
                        {results.summary.errors}
                      </p>
                      <p className="text-sm text-stone-600">Errors</p>
                    </div>
                  </div>

                  {/* Details */}
                  {results.details.enrolled.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        Enrolled ({results.details.enrolled.length})
                      </p>
                      <div className="max-h-24 overflow-y-auto text-sm text-stone-600">
                        {results.details.enrolled.slice(0, 5).join(', ')}
                        {results.details.enrolled.length > 5 && ` +${results.details.enrolled.length - 5} more`}
                      </div>
                    </div>
                  )}

                  {results.details.user_not_found.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-600" />
                        Not Found ({results.details.user_not_found.length})
                      </p>
                      <div className="max-h-24 overflow-y-auto text-sm text-stone-600">
                        {results.details.user_not_found.slice(0, 5).join(', ')}
                        {results.details.user_not_found.length > 5 && ` +${results.details.user_not_found.length - 5} more`}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>Results will appear here after enrollment</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-stone-600">
              <li className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-violet-600 mt-0.5" />
                <span>Users must already have accounts in the system to be enrolled</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-violet-600 mt-0.5" />
                <span>CSV files should have an "email" column header</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-violet-600 mt-0.5" />
                <span>TXT files should have one email per line</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminBulkEnroll;
